package org.multiplatform.kotfolio.pages.compose.side_effect

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Resources
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/side_effect/stability")
@Composable
fun StabilityPage() {
    BlogLayout(
        PageProperties(
            "Stability",
            "Stability of each parameter of your composables to work out if it can be skipped or not during recomposition."
        ),
        BlogProperties("Jetpack Compose", "Stability"))
    {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Why Optimizing Data Models in Jetpack Compose Matters.",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose makes building Uls easier than ever but if your data models aren't structured well, that convenience can come at a cost. Inefficient models can trigger unnecessary recompositions amd slow down your app.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose automatically re-renders parts of the Ul when data changes. That's powerful, but with a small field changes inside a large shared state object, the entire screen may recompose. The solution lies in how you design your data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose performs best when your models are immutable and stable. A few key principles:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Split large composables into smaller ones.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use immutable data classes with val properties.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Keep mutable state (State, Flow) as local as possible.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }


                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoid passing unstable objects into composables: mutable lists, mutable maps, objects recreated on every recomposition, lambdas created repeatedly, states that change too frequently",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Keep derived calculations out of composables when heavy.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use stable keys in Lazy lists.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoid one massive UiState that holds everything.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Pass only what each composable truly needs instead of large objects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Annotate with @Stable only if your class really meets the stability rules (all public properties immutable, doesn't expose mutable state, equals() and hashCode() behave predictably).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Optimized data models reduce wasted recompositions, make animations smoother, and improve responsiveness.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Stability of the compose compiler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose compiler analyzes each parameter for stability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stable Object means the priority doesn’t change unpredictably, and the Compiler can detect it safely.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the Object is stable, it can skip recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose’s recomposition engine needs to know when to recompose UI functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "But it can’t check every property of every object each time: that could be inefficient.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Massive State Objects",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    ❌ Anti-Pattern: Exposing the whole ViewModel state
                    @Composable
                    fun UserAvatar(uiState: ProfileUiState) {
                        // If uiState.billingHistory changes, this avatar recomposes!
                        AsyncImage(model = uiState.profileImageUrl)
                    }
                    
                    ✅ Clean Approach: Scoping to primitive needs
                    @Composable
                    fun UserAvatar(imageUrl: String) {
                        // This only recomposes if the exact URL changes.
                        AsyncImage(model = imageUrl)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Code optimization",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Same Image in 10 Files or Mores
                    
                    object AppImages {
                        val logo: Painter
                            @Composable get() = painterResource(R.drawable.ic_logo)

                        val placeholder: Painter
                            @Composable get() = painterResource(R.drawable.ic_placeholder)
                    }

                    // Now in any composable, instead of:
                    Image(painter = painterResource(R.drawable.ic_logo),)

                    // we write:
                    Image(painter = AppImages.logo, ...)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val LocalTheme = staticCompositionLocalOf<Theme> {
                        error("No theme")
                    }
                    
                    // ✅ APPROACH
                    val LocalTheme = compositionLocalOf<Theme> {
                        error("No theme")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Default Value Set
                    
                    @Composable
                    fun DatePickerDialog(
                        initialDate: LocalDate?,
                        onDateSelected: (LocalDate) -> Unit,
                    ) {
                        var selectedDate by remember { mutableStateOf(initialDate) }

                        val text = remember(selectedDate) {
                            DateTimeManager.formatLocalDateSimple(selectedDate)
                        }

                        val isError = text.isBlank()

                        TextField(value = text)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Avoid state transformations or formatting in Ul",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "Composable functions are not a place for state transformations or formatting ! Composable functions can be called many times, sometimes with every frame. Non-trivial calculations should be remembered, or even better, calculated in domain or view model and provided.",
                        modifier = Modifier.padding(topBottom = 0.3.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun Services(offer: ServiceOffer) {
                        val initials = offer.username.split(" ")
                            .map { it.first() }
                            .joinToString("")

                        val service = getServiceName(offer.serviceId)
                        val moneyDisplay = offer.amount.toMoneyFormat()
                    }
                    
                    // ✅ APPROACH
                    @Composable
                    fun Services(offerDisplay: ServiceOfferDisplay) {
                        val initials = offerDisplay.initials
                        val service = offerDisplay.service
                        val moneyDisplay = offerDisplay.moneyDisplay
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Recomposition lambda optimization - (Frequently changing state, Heavy composable tree)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // DON'T: Avoid multipurpose components
                    @Composable
                    fun Button(
                        // Problem 1: A clickable rectangle
                        onClick: () -> Unit = {},
                        // Problem 2: A checkable component
                        checked: Boolean = false,
                        onCheckedChange: (Boolean) -> Unit,
                    ) { /* ... */ }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // ❌ APPROACH A: Early Read (Prop-Drilling Raw Values)
                    @Composable
                    fun ProfileScreen(viewModel: ProfileViewModel) {
                        // ⚠️ The READ happens here because of the 'by' delegate.
                        // When the flow emits, ProfileScreen recomposes.
                        val name: String by viewModel.userName.collectAsState() 
                        ProfileCard(name) 
                    }

                    @Composable
                    fun ProfileCard(name: String) {
                        // Because the caller recomposes and passes a parameter that 
                        // fails the equality check, ProfileCard will also recompose.
                        UserAvatar()
                        UserNameText(name)
                    }
                    
                    // ✅ APPROACH B: Deferred Read (Passing State Objects)
                    @Composable
                    fun ProfileScreen(viewModel: ProfileViewModel) {
                        // ⚠️ ProfileScreen WILL recompose when the flow emits because 
                        // collectAsState() is a state-producing composable tied to this scope.
                        val nameState: State<String> = viewModel.userName.collectAsState() 
                        ProfileCard(nameState) 
                    }

                    @Composable
                    fun ProfileCard(nameState: State<String>) {
                        // ✅ NO READ HAPPENS HERE. We just pass it along.
                        // Assuming ProfileCard is skippable and receives stable
                        // parameters with equal instances, the runtime may skip executing
                        // it during recomposition. This can reduce unnecessary work in intermediate layers.
                        UserAvatar()
                        UserNameText(nameState)
                    }
                    @Composable
                    fun UserNameText(nameState: State<String>) {
                        // 🚨 THE READ HAPPENS HERE!
                        // When nameState.value changes, UserNameText is invalidated.
                        Text(text = nameState.value) 
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/video/live/urn:li:ugcPost:7429495487428423680/?originTrackingId=MBlWEOdXbCWxleD8yq82kA%3D%3D
            KotlinCode(
                code = $$"""
                    @Composable
                    fun GameScreen(vm: GameViewModel) {
                        val uiState by vm.uiState.collectAsStateWithLifecycle()
                        Header ({ uiState.level }, { uiState.livesLeft })
                        Code({ uiState.code }, { uiState.currentAnswer })
                        SelectAnswer ({ uiState.blocks }, vm::selectBlock)
                    }
                    @Composable
                    fun Header(level: () -> Int, livesLeft: Int){
                        Level(level)
                        Heart(livesLeft)
                    }

                    @Composable
                    fun Level(level: () -> Int) {
                        Text("Level ${level()}")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Lambda Modifier Variants",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier.offset(x = offsetValue) vs. Modifier.offset { IntOffset(offsetValue.roundToInt(), 0) }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier.alpha(alphaValue) vs. Modifier.graphicsLayer { alpha = alphaValue }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Modifier.drawBehind { … } (inherently lambda-based)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If the state is only read inside the lambda, Compose can often skip the composition phase for that element when the state changes. It just re-runs the layout phase (for offset) or the draw phase (for graphicsLayer or drawBehind).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/anhvt52/jetpack-compose-skills
            KotlinCode(
                code = """
                    //Defer State Reads
                    //Read state as late (as deep) as possible to narrow the recomposition scope.
                    
                    // Bad — reading scrollState here recomposes the whole parent on every scroll
                    @Composable
                    fun Parent(scrollState: ScrollState) {
                        val offset = scrollState.value
                        Child(offset = offset)
                    }

                    // Better — pass the state object, read inside the modifier lambda (deferred read)
                    @Composable
                    fun Parent(scrollState: ScrollState) {
                        Box(
                            modifier = Modifier.offset { IntOffset(0, scrollState.value) }
                            // offset lambda reads scrollState — only the layout phase reruns, not composition
                        )
                    }
                    
                    @Composable
                    fun ScrollingHeader(scrollState: ScrollState) {
                        // scrollState.value changes very frequently!
                        Text(
                            "My Header",
                            modifier = Modifier.graphicsLayer { // Read in Draw phase
                                translationY = -scrollState.value * 0.5f // Parallax effect
                                alpha = 1f - (scrollState.value / 500f).coerceIn(0f, 1f)
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SnackDetail() {
                        Box(Modifier.fillMaxSize()) { // Recomposition Scope Start
                            val scroll = rememberScrollState(0)
                            Title(snack) { scroll.value }
                        } // Recomposition Scope End
                    }

                    @Composable
                    private fun Title(snack: Snack, scrollProvider: () -> Int) {
                        val offset = with(LocalDensity.current) { scrollProvider().toDp() }
                        Column(
                            modifier = Modifier
                                .offset(y = offset)
                        ) {
                            
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/anhvt52/jetpack-compose-skills
            KotlinCode(
                code = """
                    //Unstable — new lambda instance on every recomposition
                    LazyColumn {
                        items(books, key = { it.id }) { book ->
                            BookCard(book, onClick = { navigator.navigate(book.id) }) // new lambda each time
                        }
                    }
                    
                    //Even Better Pattern (Recommended)
                    val onBookClick: (String) -> Unit = remember(navigator) {
                        navigator::navigate
                    }
                    
                    LazyColumn {
                       items(books, key = { it.id }) { book ->
                           BookCard(book, onClick = onBookClick)
                       }
                    }
                   
                    // Stable — remembered lambda
                    val onBookClick = remember(navigator) { { bookId: String -> navigator.navigate(bookId) } }
                    LazyColumn {
                        items(books, key = { it.id }) { book ->
                            BookCard(book, onClick = { onBookClick(book.id) })
                        }
                    }
                    
                    @Composable
                    fun BookItem(
                        book: Book,
                        onClick: (String) -> Unit
                    ) {
                        Text(
                            text = book.title,
                            modifier = Modifier.clickable {
                                onClick(book.id)
                            }
                        )
                    }
                    
                    @Composable
                    fun BookItem(book: Book, onClick: (String) -> Unit) {
                        Text(
                            text = book.title,
                            modifier = Modifier.clickable {
                                onClick(book.id)
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://github.com/smithjustinn84-netizen/Memory-Match.git
            KotlinCode(
                code = $$"""
                    // ✅ APPROACH
                    @Stable
                    data class SettingsUIState(
                        val soundVolume: Float = 1.0f,
                        val musicVolume: Float = 1.0f,
                    )
                    
                    VolumeSlider(
                       value = state.musicVolume, //value = { } need to call a function every time, which adds unnecessary indirection.
                       onValueChange = { },
                    )
                    
                    @Composable
                    private fun VolumeSlider(
                        value: Float,
                        onValueChange: (Float) -> Unit,
                        modifier: Modifier = Modifier,
                    ) {
                        Row(
                            modifier =
                                modifier
                                    .fillMaxWidth()
                                    .padding(top = 8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                        ) {
                            Icon(
                                imageVector = AppIcons.VolumeUp,
                                contentDescription = null,
                                tint = ,
                                modifier = Modifier.size(18.dp),
                            )
                            
                            Slider(
                                value = value,
                                onValueChange = onValueChange,
                                modifier = Modifier.weight(1f),
                                colors =
                                    SliderDefaults.colors(
                                        thumbColor = Color.White,
                                        activeTrackColor = ,
                                        inactiveTrackColor = ,
                                    ),
                            )
                            Text(
                                text = "${(value * 100).toInt()}%",
                                style = MaterialTheme.typography.labelMedium,
                                color = Color.White.copy(alpha = 0.6f),
                                modifier = Modifier.width(32.dp),
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/video/live/urn:li:ugcPost:7429495487428423680/?originTrackingId=MBlWEOdXbCWxleD8yq82kA%3D%3D
            KotlinCode(
                code = """
                    @Composable
                    fun ContactsScreen(contacts: PersistentList<String>) {

                        val gridState = rememberLazyGridState()

                        val isAtTop by remember {
                            derivedStateOf { gridState.firstVisibleItemIndex == 0 }
                        }

                        Scaffold(
                            floatingActionButton = {
                                if (!isAtTop) {
                                    ScrollToTopFloatingActionButton(gridState)
                                }
                            }
                        ) { paddingValues ->

                            ContactsList(
                                gridState = gridState,
                                contacts = contacts,
                                modifier = Modifier.padding(paddingValues)
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/video/live/urn:li:ugcPost:7429495487428423680/?originTrackingId=MBlWEOdXbCWxleD8yq82kA%3D%3D
            KotlinCode(
                code = """
                    @Composable
                    fun ListWithHeader() {
                        val listState = rememberLazyListState()
                        
                        ListHeader(
                            modifier = Modifier.fillMaxWidth(),
                            currentOffset = { listState.firstVisibleItemScrollOffset }
                        )   
                    }
                    
                    @Composable
                    fun ListHeader(
                        modifier: Modifier,
                        currentOffset: () -> Int //optimization 
                    ) {

                        val offsetModifier = Modifier.graphicsLayer {
                            val distance = 250.dp.toPx()
                            val offset = currentOffset().toFloat()
                            val usable = if (offset < distance) {
                                offset
                            } else {
                                (offset - (offset - distance))
                            }

                            translationY = usable
                        }

                        Box(
                            modifier = offsetModifier.then(modifier),
                            contentAlignment = Alignment.Center
                        ) {
                            Box(
                                modifier = modifier
                                    .graphicsLayer {
                                        val offset = currentOffset().toFloat()
                                        val offsetScale = offset * 0.083F
                                        val scale = (1f - offsetScale).coerceIn(.2f, 1f)
                                        scaleX = scale
                                        scaleY = scale
                                        val maxBound = 250.dp.toPx()
                                        translationY = offset.coerceIn(0f, maxBound).times(-1f)
                                    }
                                    .size(250.dp)
                                    .clip(CircleShape)
                                    .background(Color.LightGray)
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://www.linkedin.com/video/live/urn:li:ugcPost:7429495487428423680/?originTrackingId=MBlWEOdXbCWxleD8yq82kA%3D%3D
            KotlinCode(
                code = """
                    interface OrderTracker {
                        fun getDetailedStatus(): String 
                        val detailedStatus: StateFlow<String>
                    }
                    
                    @Composable
                    fun OrderTrackingScreen(
                        tracker: OrderTracker,
                        modifier: Modifier = Modifier
                    ) {
                        BoxWithConstraints(
                            modifier = modifier
                                .fillMaxSize()
                                .padding(all = 16.dp)
                        ) {

                            val transition = rememberInfiniteTransition(label = "truck_transition")

                            val truckOffset by transition.animateFloat(
                                initialValue = 0f,
                                targetValue = maxWidth.value - 48.dp.value,
                                animationSpec = infiniteRepeatable(
                                    animation = tween(
                                        durationMillis = 3000,
                                        easing = LinearEasing
                                    ),
                                    repeatMode = RepeatMode.Reverse
                                ),
                                label = "truck_offset"
                            )

                            //val status = tracker.getDetailedStatus()
                            //val status = { tracker.getDetailedStatus() }
                            
                            val status by tracker.detailedStatus.collectAsStateWithLifecycle()

                            Box(
                                modifier = Modifier
                                    .offset(x = truckOffset.dp)
                            ) {
                                Icon(
                                    imageVector = . 
                                    modifier = Modifier
                                        .size(48.dp)
                                        //.offset(x = truckOffset.dp)
                                        .offset { IntOffset(truckOffset.dp.roundToPx(), 0) }
                                )
                            }
                        }
                    }
                    
                    //Text(status())
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    MyScreen(
                        onEvent = { event -> viewModel.sendIntent(event) }
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val toggleLabels = listOf("Notifications", "Dark Mode", "Auto-Save")
                    val toggleStates = remember {
                       List(toggleLabels.size) { mutableStateOf(false) }
                    }
                    
                    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                        toggleLabels.forEachIndexed { index, label ->
                            ToggleableCard(
                                label = label,
                                isChecked = toggleStates[index].value,
                                onToggle = { toggleStates[index].value = !toggleStates[index].value },
                            )
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun AppScaffold(
                        modifier: Modifier = Modifier,
                        contentInsets: WindowInsets = WindowInsets.safeDrawingHorizontalOnly,
                        bottomBar: @Composable () -> Unit = { },
                        content: @Composable BoxScope.(PaddingValues) -> Unit,
                    ) {
                        Scaffold(
                            contentWindowInsets = contentInsets,
                            modifier = modifier,
                            bottomBar = bottomBar,
                            snackbarHost = {
                               
                            },
                            containerColor = AppTheme.colors.surface,
                            content = {
                                val paddingValues = WindowInsets.safeDrawing
                                    .exclude(contentInsets)
                                    .asPaddingValues()
                                Box(Modifier
                                    .fillMaxSize()
                                    .background(AppTheme.colors.surface)
                                    .windowInsetsPadding(contentInsets)
                                    .consumeWindowInsets(contentInsets)
                                ) {
                                    content(paddingValues)
                                }
                            }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Not recommended - causes recomposition of entire Modifier chain
                    Modifier.background(if (isSelected) Color.Green else Color.Gray)
                    
                    //Recommended
                    Modifier.drawBehind {
                        if (isSelected) drawRect (Color.Green) else drawRectColor.Gray)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun VerticalScrollerState(): VerticalScrollerState = VerticalScrollerStateImpl()
                    
                    @Stable
                    interface VerticalScrollerState {
                        var scrollPosition: Int
                        var scrollRange: Int
                    }
                    
                    private class VerticalScrollerStateImpl(
                        scrollPosition: Int = 0,
                        scrollRange: Int = 0
                    ) : VerticalScrollerState {

                        private var _scrollPosition by mutableStateOf(scrollPosition)

                        override var scrollRange by mutableStateOf(scrollRange)

                        override var scrollPosition: Int
                            get() = _scrollPosition
                            set(value) {
                                _scrollPosition = value.coerceIn(0, scrollRange)
                            }
                    }
                    
                    @Composable
                    fun VerticalScroller(
                        verticalScrollerState: VerticalScrollerState =
                            remember { VerticalScrollerState() }
                    ) {
                        val scrollPosition = verticalScrollerState.scrollPosition
                        val scrollRange = verticalScrollerState.scrollRange

                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun ScrollAwareHeader(
                        scrollOffsetProvider: () -> Int
                    )
                        //val offset = scrollOffsetProvider()
                        //val offset by remember { derivedStateOf { scrollOffsetProvider() } }
                        val listState = rememberLazyListState()
                   
                        ScrollAwareHeader(
                            // ✅ Current scope avoids recomposition
                            scrollOffsetProvider = { listState.firstVisibleItemScrollOffset }
                        )
                    )
                    
                    //2
                    @Composable
                    fun ScrollAwareHeader(
                        scrollOffsetProvider: () -> Int
                    ) {
                        val offset by remember {
                            derivedStateOf { scrollOffsetProvider() }
                        }
                        
                        Text(text = "Offset: $offset")
                    }

                    @Composable
                    fun Screen() {
                        val listState = rememberLazyListState()

                        Column {
                            ScrollAwareHeader(
                                // ✅ Only this lambda is read inside header
                                scrollOffsetProvider = { listState.firstVisibleItemScrollOffset }
                            )

                            LazyColumn(
                                state = listState
                            ) {
                                items(100) {
                                    Text("Item $it")
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            //https://medium.com/@ramadan123sayed/optimizing-a-laggy-feed-timeline-screen-in-jetpack-compose-fixing-12-common-performance-issues-948e7835b4ac
            KotlinCode(
                code = """
                    //Swipe to Refresh
                    
                    @Immutable
                    data class FeedUiState(
                        val posts: ImmutableList<PostUi> = persistentListOf(),
                        val isRefreshing: Boolean = false,
                        val isLoadingMore: Boolean = false,
                        val currentUserId: String = "",
                        val endReached: Boolean = false,
                        val error: String? = null
                    )

                    @Immutable
                    sealed class FeedEvent {
                        data object Refresh : FeedEvent()
                        data object LoadMore : FeedEvent()
                        @JvmInline value class ToggleLike(val postId: String) : FeedEvent()
                        @JvmInline value class ToggleBookmark(val postId: String) : FeedEvent()
                        @JvmInline value class SharePost(val postId: String) : FeedEvent()
                        @JvmInline value class OpenComments(val postId: String) : FeedEvent()
                        @JvmInline value class OpenProfile(val userId: String) : FeedEvent()
                    }
                    
                    @Immutable
                    sealed class FeedEffect {
                        @JvmInline value class NavigateToComments(val postId: String) : FeedEffect()
                        @JvmInline value class NavigateToProfile(val userId: String) : FeedEffect()
                        @JvmInline value class ShareSheet(val url: String) : FeedEffect()
                        @JvmInline value class ShowError(val message: String) : FeedEffect()
                    }

                    // In ViewModel
                    private val _state = MutableStateFlow(FeedUiState())
                    val state: StateFlow<FeedUiState> = _state.stateIn(
                        scope = viewModelScope,
                        started = SharingStarted.WhileSubscribed(5000),
                        initialValue = FeedUiState()
                    )
                    
                    @Composable
                    fun LaggyFeedScreen(viewModel: FeedViewModel) {
                        // 🚫 PROBLEM #1: Multiple state collections, not lifecycle-aware
                        val posts = viewModel.posts.collectAsState().value
                        val isRefreshing = viewModel.isRefreshing.collectAsState().value
                        val currentUser = viewModel.currentUser.collectAsState().value
                        val isLoadingMore = viewModel.isLoadingMore.collectAsState().value
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun OptimizedFeedScreen(viewModel: FeedViewModel) {
                        val state by viewModel.state.collectAsStateWithLifecycle()
                        val pullRefreshState = rememberPullToRefreshState()

                    PullToRefreshBox(
                            isRefreshing = state.isRefreshing,
                            onRefresh = { viewModel.onEvent(FeedEvent.Refresh) },
                            state = pullRefreshState
                        ) {
                            LazyColumn(
                                state = listState,
                                contentPadding = PaddingValues(vertical = 8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                items(items = state.posts, key = { it.id }, contentType = { it.contentType }) { post ->
                                    PostCard(post = post, ...)
                                }
                            }
                        }
                    }
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun OptimizedFeedScreen(viewModel: FeedViewModel, modifier: Modifier = Modifier) {
                        val state by viewModel.state.collectAsStateWithLifecycle()
                        val listState = rememberLazyListState()
                        val pullRefreshState = rememberPullToRefreshState()

                    val onLikeClick = remember { { id: String -> viewModel.onEvent(FeedEvent.ToggleLike(id)) } }
                        val onBookmarkClick = remember { { id: String -> viewModel.onEvent(FeedEvent.ToggleBookmark(id)) } }
                        val onShareClick = remember { { id: String -> viewModel.onEvent(FeedEvent.SharePost(id)) } }
                        val onCommentClick = remember { { id: String -> viewModel.onEvent(FeedEvent.OpenComments(id)) } }
                        val onProfileClick = remember { { id: String -> viewModel.onEvent(FeedEvent.OpenProfile(id)) } }
                        val shouldLoadMore by remember {
                            derivedStateOf {
                                val lastVisible = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
                                lastVisible >= state.posts.size - 5 && !state.isLoadingMore && !state.endReached
                            }
                        }
                        LaunchedEffect(shouldLoadMore) {
                            if (shouldLoadMore) viewModel.onEvent(FeedEvent.LoadMore)
                        }
                        PullToRefreshBox(
                            isRefreshing = state.isRefreshing,
                            onRefresh = { viewModel.onEvent(FeedEvent.Refresh) },
                            state = pullRefreshState
                        ) {
                            LazyColumn(
                                state = listState,
                                modifier = modifier.fillMaxSize(),
                                contentPadding = PaddingValues(vertical = 8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                items(
                                    items = state.posts,
                                    key = { it.id },
                                    contentType = { it.contentType }
                                ) { post ->
                                    PostCard(
                                        post = post,
                                        onLikeClick = onLikeClick,
                                        onBookmarkClick = onBookmarkClick,
                                        onShareClick = onShareClick,
                                        onCommentClick = onCommentClick,
                                        onProfileClick = onProfileClick,
                                        modifier = Modifier.animateItem()
                                    )
                                }
                                if (state.isLoadingMore) {
                                    item(key = "loading_more", contentType = "loading") {
                                        Box(Modifier.fillMaxWidth().padding(16.dp), contentAlignment = Alignment.Center) {
                                            CircularProgressIndicator(Modifier.size(24.dp))
                                        }
                                    }
                                }
                            }
                        }
                    }
                    
                    //loadmore
                    val shouldLoadMore by remember {
                        derivedStateOf {
                            val lastVisible = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: 0
                            lastVisible >= state.posts.size - 5 && !state.isLoadingMore && !state.endReached
                        }
                    }

                    LaunchedEffect(shouldLoadMore) {
                        if (shouldLoadMore) viewModel.onEvent(FeedEvent.LoadMore)
                    }
                    """.trimIndent()
            )

            //https://github.com/ShanuDevCodes/KritiqueRevemp
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3ExpressiveApi::class, ExperimentalMaterial3Api::class)
                    @Composable
                    fun FacultyScreen() {
                        val scope = rememberCoroutineScope()
                        val searchBarState = rememberSearchBarState()
                        val textFieldState = rememberTextFieldState()
                        val configuration = LocalConfiguration.current
                        val screenWidthDp = configuration.screenWidthDp.dp
                        val isPortrait = configuration.orientation == Configuration.ORIENTATION_PORTRAIT
                        val inputField =
                            @Composable {
                                SearchBarDefaults.InputField(
                                    colors = SearchBarDefaults.inputFieldColors(
                                        focusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                                        unfocusedContainerColor = MaterialTheme.colorScheme.surfaceContainerHigh,
                                    ),
                                    modifier = Modifier
                                        .width(
                                            width = when (isPortrait) {
                                                false -> screenWidthDp * 0.337f
                                                true -> screenWidthDp
                                            }
                                        ),
                                    searchBarState = searchBarState,
                                    textFieldState = textFieldState,
                                    onSearch = {
                                        scope.launch {
                                            searchBarState.animateToCollapsed()
                                        }
                                    },
                                    placeholder = {
                                        Text(
                                            text = "Kritique",
                                            style = MaterialTheme.typography.titleMediumEmphasized,
                                            textAlign = TextAlign.Center,
                                            modifier = Modifier.fillMaxWidth()
                                        )
                                    },
                                    leadingIcon = {
                                        if (searchBarState.currentValue == SearchBarValue.Expanded) {
                                            IconButton(
                                                onClick = {
                                                    scope.launch {
                                                        textFieldState.clearText()
                                                        searchBarState.animateToCollapsed()
                                                    }
                                                }
                                            ) {
                                                Icon(
                                                    Icons.AutoMirrored.Default.ArrowBack,
                                                    contentDescription = "Back"
                                                )
                                            }
                                        } else {
                                            IconButton(
                                                onClick = {
                                                    scope.launch {
                                                        searchBarState.animateToExpanded()
                                                    }
                                                }
                                            ) {
                                                Icon(Icons.Default.Search, contentDescription = null)
                                            }
                                        }
                                    },
                                    trailingIcon = {
                                        Box(
                                            modifier = Modifier
                                                .size(40.dp)
                                                .clip(CircleShape)
                                                .background(MaterialTheme.colorScheme.primary)
                                                .clickable(onClick = {
                                                    scope.launch {
                                                        searchBarState.animateToCollapsed()
                                                    }
                                                }),
                                            contentAlignment = Alignment.Center
                                        ) {
                                            Icon(
                                                imageVector = Icons.AutoMirrored.Filled.ArrowForward,
                                                contentDescription = "Search",
                                                tint = MaterialTheme.colorScheme.surfaceContainerLow,
                                                modifier = Modifier.size(24.dp)
                                            )
                                        }
                                    },
                                )
                            }
                    }
                    """.trimIndent()
            )

            //How snapshot state delegation and destructuring works?
            //https://mail.google.com/mail/u/0/?tab=rm&ogbl#search/recomposition/FMfcgzQfBslFPRRkkMTLZFBgbqxcSLrW
            //https://github.com/thementalgoose/kmp-flashback.git (Search)
            KotlinCode(
                code = """
                    val state1 = remember { mutableStateOf( value = "") } 
                    TextField(value = state1.value, onValueChange = { state1. value = it })
            
                    var state2 by remember { mutableStateOf( value = "") }
                    //TextField(value = state2,  onValueChange = { newValue -> state2 = newValue })
                    TextField(value = state2, onValueChange = { state2 = it }) //Short Syntax
            
                    val (value3, setValue3) = remember { mutableStateOf( value = "") }
                    TextField(value = value3, onValueChange = setValue3)
                    
                    //New
                    var searchText by remember { mutableStateOf(TextFieldValue("")) }
                    
                    //Search
                    val searchTerm = remember { mutableStateOf(TextFieldValue(uiState.searchQuery ?: "")) }
                    TextInput(
                        text = searchTerm,
                        onValueChange = {
                            searchTerm.value = it
                        }
                    )
                    
                    @Composable
                    fun TextInput(
                        text: MutableState<TextFieldValue>,
                        onValueChange: (TextFieldValue) -> Unit = { text.value = it },
                    ) {
                        BasicTextField(
                            value = text.value,
                            onValueChange = onValueChange,
                        )        
                     }
               """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The first approach is straightforward: mutableStateOf creates snapshot state instance that we use to store state. Snapshot state guarantees component recomposition when state changes, remember persists the same state object across recompositions. This is the most rudimentary way to store local state, but there are two ways to simplify this state reading or modification.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The second approach uses property delegation. When we use by keyword after a variable, all reads of this variable are just a call of getValue on the object on the right, and all writes are calls of setValue of this object. In consequence, reading and writing state is easier, and the behavior is the same as in the first approach.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The third approach isn’t popular in Compose. I believe it was inspired by how is state defined in React. It is MutableState destructuring into value and setter. This regular bracket is used for position-based destructuring, that just calls componentN (where N is position index) for each variable inside the bracket. MutableState provides component1 that just calls value, and component2 that creates a setter.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/pbclive-digital/pbc-web-app.git (contentText)
            KotlinCode(
                code = """
                    //TextFieldValue Search/Filter

                    @Composable
                    @Preview
                    fun App() {
                        MaterialTheme {
                            var searchText by remember { mutableStateOf(TextFieldValue("")) }

                            val commands = listOf(
                                "Open App",
                                "Search Files",
                                "View System Status",
                                "Run Command",
                                "Settings"
                            )

                            Column(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(16.dp)
                            ) {
                                SearchBox(searchText) { searchText = it }
                                CommandList(commands.filter { it.contains(searchText.text, ignoreCase = true) })
                            }
                        }
                    }
                    
                    /*
                        val firstName = remember { mutableStateOf(TextFieldValue()) }
                        contentText = firstName
                    */

                    @Composable
                    fun SearchBox(
                        //contentText: MutableState<TextFieldValue>,
                        searchText: TextFieldValue, 
                        onSearchTextChanged: ((newValue: TextFieldValue) -> Unit)? = null) 
                     {
                        OutlinedTextField(
                            value = searchText,
                            //value = contentText.value,
                            onValueChange = { newValue ->
                                onValueChange?.invoke(newValue)
                            },
                            label = { Text("Search") },
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFF2C2C2C))
                                .padding(16.dp),
                            singleLine = true,
                            textStyle = MaterialTheme.typography.h6.copy(color = Color.White),
                            colors = TextFieldDefaults.outlinedTextFieldColors(
                                backgroundColor = Color(0xFF2C2C2C),
                                focusedBorderColor = Color(0xFF4CAF50),
                                unfocusedBorderColor = Color.Gray
                            )
                        )
                    }

                    @Composable
                    fun CommandList(commands: List<String>) {
                        val scrollState = rememberScrollState()

                        Column(modifier = Modifier.verticalScroll(scrollState)) {
                            commands.forEach { command ->
                                CommandItem(command)
                                Spacer(modifier = Modifier.height(10.dp))
                            }
                        }
                    }

                    @Composable
                    fun CommandItem(command: String) {
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(8.dp)
                                .background(Color(0xFF333333)),
                            elevation = 4.dp
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(16.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Text(command, style = MaterialTheme.typography.body1, color = Color.Black)
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun UserNameTextField(
                        value: String,
                        onChange: (String) -> Unit,
                        modifier: Modifier = Modifier,
                        hint: String?,
                        onRemoveButtonClick: () -> Unit,
                    ) = CompositionLocalProvider(
                        LocalTextSelectionColors provides
                            TextSelectionColors(
                                handleColor = Color.White,
                                backgroundColor = Color.White,
                            ),
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Safer pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Tips Smart Work
            KotlinCode(
                code = """
                    //Compose uses referential equality to decide recomposition.
                    //New lambda = potential recomposition.
                    
                    //1
                    val onUserClick: (String) -> Unit = remember {
                        { id -> viewModel.onUserClick(id) }
                    }  // ❌ captures old VM 

                    items(users) { user ->
                        UserItem(
                            user = user,
                            onClick = { onUserClick(user.id) }
                        )
                    }
                    
                    //2. Correct pattern if viewModel may change
                    val latestVm by rememberUpdatedState(viewModel)

                    val onUserClick: (String) -> Unit = remember {
                        { id -> latestVm.onUserClick(id) }
                    }
                    
                    //3.
                    val onUserClick: (String) -> Unit = viewModel::onUserClick
                    """.trimIndent()
            )

            SpanText(
                text = "ImmutableList",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ImmutableList - A list type from the kotlinx.collections.immutable package that cannot be modified once created.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "persistentListOf() - A factory function that creates an empty PersistentList, which is both immutable and efficient for structural sharing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Shorter form - val employers = persistentListOf<Employer>()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    kotlin-immutable = "0.4.0"
                    
                    [libraries]
                    kotlinx-collections-immutable = { module = "org.jetbrains.kotlinx:kotlinx-collections-immutable", version.ref = "kotlin-immutable" }
                    
                    sourceSets {
                        commonMain.dependencies {
                        implementation(libs.kotlinx.collections.immutable)
                    }
                    
                    data class Employer(val name: String, val company: String)
                    
                    val employers: ImmutableList<Employer> = persistentListOf(
                        Employer("Navnit", "Amazon")
                    )
                    
                    @Stable
                    data class SearchUiState(
                        val referenceTable: PersistentList<Reference> = persistentListOf()
                    )  
                      
                    val products = remember {
                        List(100) {
                            Product(it, "Product $it", it * 10.0)
                        }.toPersistentList()
                    }  
                    """.trimIndent()
            )

            //https://github.com/Montfel/FIPE.git
            KotlinCode(
                code = """
                    //domain/model
                    data class Models(
                        val models: PersistentList<Model>,
                        val yearModels: PersistentList<YearModel>,
                    )
                    
                    data class Model(
                        val name: String,
                        val code: String,
                    )
                    
                    data class YearModel(
                        val name: String,
                        val code: String,
                    )
                    
                    //data/mapper
                    internal fun ModelsData.toModels(): Models {
                        return Models(
                            models = models.map(ModelData::toModel).toPersistentList(),
                            yearModels = yearModels.map(YearModelData::toYearModel).toPersistentList()
                        )
                    }
                    
                    @Serializable
                    internal data class ModelsData(
                        @SerialName("Modelos")
                        val models: List<ModelData>,

                        @SerialName("Anos")
                        val yearModels: List<YearModelData>,
                    )
                    
                    @Serializable
                    internal data class ModelData(
                        @SerialName("Label")
                        val name: String,

                        @SerialName("Value")
                        val code: Int,
                    )
                    
                    @Serializable
                    internal data class YearModelData(
                        @SerialName("Label")
                        val name: String,

                        @SerialName("Value")
                        val code: String,
                    )
                    
                    internal interface SearchService {
                        suspend fun getModels(
                            referenceTable: String,
                            vehicleType: String,
                            brand: String
                        ): ModelsData
                    }
                    
                    internal class SearchServiceImpl(
                        private val httpProvider: HttpProvider
                    ) : SearchService {
                        override suspend fun getModels(
                            referenceTable: String,
                            vehicleType: String,
                            brand: String
                        ): ModelsData {
                            val response = httpProvider().post(MODEL) {
                                parameter(REFERENCE_TABLE_PARAMETER, referenceTable)
                                parameter(VEHICLE_TYPE_PARAMETER, vehicleType)
                                parameter(BRAND_PARAMETER, brand)
                            }
                            return response.body<ModelsData>()
                        }
                    }
                    
                    interface SearchRepository {
                        suspend fun getModels(
                            referenceTable: String,
                            vehicleType: String,
                            brand: String
                        ): Result<Models>
                    }
                    
                    internal class SearchRepositoryImpl(
                        private val searchService: SearchService
                    ) : SearchRepository {

                        override suspend fun getModels(
                            referenceTable: String,
                            vehicleType: String,
                            brand: String
                        ): Result<Models> {
                            return runCatching {
                                searchService.getModels(
                                    referenceTable = referenceTable,
                                    vehicleType = vehicleType,
                                    brand = brand
                                ).toModels()
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Stable & Immutable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Immutable: Compose marks a type as immutable if the value of its properties can never change and all methods are referentially transparent. Note that all primitive types are marked as immutable. These include String, Int, and Float.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Stable: Indicates a type whose properties can change after construction. If and when those properties change during runtime, Compose becomes aware of those changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@Stable - (Trusted between observable changes)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These hold data that is mutable, but notify Composition upon mutating. This renders them stable since Composition is always informed of any changes to state. The kotlinx.collections.immutable library, which includes those two as well as other PersistentCollection and ImmutableCollection interfaces",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Indicates a mutable type, but the Compose runtime will be notified if and when any public property or method behavior produces different results than a previous invocation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose optimizes performance by skipping the recomposition process for these stable parameters. For example; Int, String, Float, ImmutableList types are stables parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Counter for likes, ToggleState for notifications, or any mutable state inside a class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/P0mid00r/WeatherApp
            KotlinCode(
                code = """
                    sealed interface NetworkState {
                        data object Connected : NetworkState
                        data object Disconnected : NetworkState
                    }

                    @Stable
                    class NetworkChecker(context: Context) {
                        private val _networkState = MutableStateFlow<NetworkState>(NetworkState.Disconnected)
                        val networkState = _networkState.asStateFlow()

                        val isConnected
                            get() = networkState.value == NetworkState.Connected

                        private val connectivityManager = context.getSystemService(Context.CONNECTIVITY_SERVICE) as ConnectivityManager
                        private val validNetworks = ConcurrentHashMap.newKeySet<Network>()

                        private val callback = object : ConnectivityManager.NetworkCallback() {
                            override fun onCapabilitiesChanged(network: Network, networkCapabilities: NetworkCapabilities) {
                                val isValid = networkCapabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET) &&
                                        networkCapabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_VALIDATED)

                                if (isValid) {
                                    validNetworks.add(network)
                                } else {
                                    validNetworks.remove(network)
                                }
                                updateState()
                            }

                            override fun onLost(network: Network) {
                                validNetworks.remove(network)
                                updateState()
                            }
                        }

                        init {
                            _networkState.update {
                                if (hasValidNetwork()) NetworkState.Connected else NetworkState.Disconnected
                            }
                        }

                        private fun hasValidNetwork(): Boolean {
                            val network = connectivityManager.activeNetwork ?: return false
                            val capabilities = connectivityManager.getNetworkCapabilities(network) ?: return false
                            return capabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_INTERNET) &&
                                    capabilities.hasCapability(NetworkCapabilities.NET_CAPABILITY_VALIDATED)
                        }

                        private fun updateState() {
                            val isConnected = validNetworks.isNotEmpty()
                            _networkState.value = if (isConnected) NetworkState.Connected else NetworkState.Disconnected
                        }

                        fun register() {
                            connectivityManager.registerNetworkCallback(
                                NetworkRequest.Builder().build(),
                                callback
                            )
                        }

                        fun unregister() {
                            connectivityManager.unregisterNetworkCallback(
                                callback
                            )
                            validNetworks.clear()
                        }
                    }
                    
                    class WeatherViewModel(private val applicationContext: Context) : ViewModel() {
                        val networkChecker = NetworkChecker(applicationContext)
                        
                        fun loadWeatherInfo() {
                            viewModelScope.launch(Dispatchers.Default) {
                                val networkCheckerJob = viewModelScope.launch {
                                    if (!networkChecker.isConnected) {
                                        networkChecker.networkState.collect { state ->
                                            when (state) {
                                                NetworkState.Connected -> cancel()
                                                NetworkState.Disconnected -> null
                                            }
                                        }
                                    }
                                }
                                networkCheckerJob.join()
                            }
                        }
                    }
                    
                    //UI
                    val networkState by viewModel.networkChecker.networkState.collectAsState()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Stable
                    data class HomeState(
                        val productList: List<Product>,
                        val isLoading: Boolean = false,
                    )
                    """.trimIndent()
            )

            //https://github.com/stoyan-vuchev/magic-message.git
            KotlinCode(
                code = """
                    @Stable
                    @Serializable
                    data class CreationModel(
                        val id: Long?,
                        val createdAt: Long,
                        val isDraft: Boolean,
                        val isFavorite: Boolean,
                        
                        @Stable val drawConfiguration: DrawConfiguration,
                        @Stable val drawingSnapshot: DrawingSnapshot
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Stable + @Immutable together ❌
                    
                    @Stable
                    @Immutable
                    data class ChatScreenState(
                        val chatOrb: ChatOrb = ChatOrb.NORMAL,
                        val chatMessages: List<ChatMessage> = listOf(),
                        val mystery: Mystery? = null,
                        val isLoadingNewMessage: Boolean = false,
                        val isGameEnd: Boolean = false,
                        val snackBarHostState: SnackbarHostState = SnackbarHostState()
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "kotlinx.collections.immutable library stable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "kotlinx.collections.immutable library, like PersistentList, PersistentSet or PersistentMap, which are stable as they represent truly immutable collections.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //stable 
                   
                    @Composable
                    fun CollectionsComponent(
                        list: PersistentList<User> stable, 
                        set: PersistentSet<User>,
                        map: PersistentMap<String, User> stable ,
                    )
                    
                    //Runtime
                    @Composable
                    fun CollectionsComponent(
                        //runtime
                        list: List<User>
                        set: Set<User>, 
                        map: map<String, User>,
                        
                        //stable
                        modifier: Modifier stable = Modifier
                    ) { }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Another solution is to set stability configuration
                    composeCompiler {
                        stabilityConfigurationFiles =
                            listOf(rootProject.layout.projectDirectory.file( path ="stability_config.conf"))
                    ｝

                    // stability_config.conf
                    kotlin.collections.List
                    kotlin.collections.Set
                    kotlin collections Map
                    """.trimIndent()
            )

            //https://github.com/droidwithme/product-catalog.git
            KotlinCode(
                code = """
                    @Stable
                    public sealed class ViewState<out T> {
                        public data object Loading : ViewState<Nothing>()
                        public data class Success<T>(val data: T) : ViewState<T>()
                        public data class Error(val error: Throwable?) : ViewState<Nothing>()
                    }

                    public fun <T> List<T>.toViewState(): ViewState<List<T>> = when {
                        this.isEmpty() -> ViewState.Loading
                        else -> ViewState.Success(data = this)
                    }

                    public fun <T> Result<T>.toViewState(): ViewState<T> = when (this) {
                        Result.Loading -> ViewState.Loading
                        is Result.Error -> ViewState.Error(error = this.exception)
                        is Result.Success -> ViewState.Success(data = this.value)
                    }

                    public fun <T> Result<PaginatedData<T>>.toViewStatePaginated(): ViewState<List<T>> = when (this) {
                        Result.Loading -> ViewState.Loading
                        is Result.Error -> ViewState.Error(error = this.exception)
                        is Result.Success -> ViewState.Success(data = this.value.data)
                    }
                    
                    @Stable
                    internal data class HomeViewState(
                        val productsViewState: ViewState<List<Products>> = ViewState.Loading,
                        val searchResults: ViewState<List<Products>> = ViewState.Success(emptyList()),
                        val searchQuery: String = "",
                        val searchActive: Boolean = false,
                        val error: Throwable? = null,
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "@Immutable - (Total trust, always skipped)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose relies on stability and immutability to optimize recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Your class or data structure is truly immutable (all properties are `val` and any nested objects are also immutable). You’re working with state objects or data models that are shared between composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "@Immutable annotation, indicates that all public fields are immutable since they are created, and can be applied to a class.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Indicates a type where the value of any property will never change after the object is constructed and all methods are referentially transparent. All primitive types (String, Int, Float, etc) are considered immutable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is a stronger promise than Kotlin’s val keyword. In the case of val, the instance is the same but the inside value can change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When you update it, you create a new instance using copy(). When a composable receives an immutable object, Compose can quickly compare it with the previous object. If they are different instances, it triggers a recomposition. If they are the same, Compose can confidently skip the recomposition for that sub-tree, saving a lot of performance cost. Using mutable lists or mutable data classes confuses Compose’s tracking and can lead to bugs or over-recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://grippo-app.github.io/grippo-site (snapshotFlow pagination, Validation Good, Koin Module, Token Manage Mutex, Custom Theme, Custom Compose, KTOR (runcatching, Dispatchers.io), Context App Level, Google/Apple Login, Segment, Wheel Multiple Compose Weight Picker, Body Part Select) (code - https://github.com/Grippo-app/grippo-mobile.git)
            KotlinCode(
                code = """
                    //Custom Style
                    //Modifier.scalableClick: https://multiplatformdeveloper.netlify.app/compose_basic
                    
                    @Immutable
                    public sealed class CheckSelectableCardStyle(
                        public open val title: String
                    ) {
                        @Immutable
                        public data class Small(
                            override val title: String,
                        ) : CheckSelectableCardStyle(title)

                        @Immutable
                        public data class Medium(
                            override val title: String,
                            val description: String?,
                        ) : CheckSelectableCardStyle(title)

                        @Immutable
                        public data class Large(
                            override val title: String,
                            val description: String,
                            val icon: ImageVector,
                        ) : CheckSelectableCardStyle(title)
                    }
                    
                    @Composable
                    public fun CheckSelectableCard(
                        modifier: Modifier = Modifier,
                        style: CheckSelectableCardStyle,
                        isSelected: Boolean,
                        onSelect: () -> Unit
                    ) {
                        when (style) {
                            is CheckSelectableCardStyle.Large -> CheckSelectableCardLarge(
                                modifier = modifier,
                                style = style,
                                isSelected = isSelected,
                                onClick = onSelect
                            )

                            is CheckSelectableCardStyle.Medium -> CheckSelectableCardMedium(
                                modifier = modifier,
                                style = style,
                                isSelected = isSelected,
                                onClick = onSelect
                            )

                            is CheckSelectableCardStyle.Small -> CheckSelectableCardSmall(
                                modifier = modifier,
                                style = style,
                                isSelected = isSelected,
                                onClick = onSelect
                            )
                        }
                    }
                    
                    @Composable
                    internal fun CheckSelectableCardLarge(
                        modifier: Modifier,
                        style: CheckSelectableCardStyle.Large,
                        isSelected: Boolean,
                        onClick: () -> Unit
                    ) {
                        val shape = RoundedCornerShape(AppTokens.dp.checkSelectableCard.large.radius)

                        val borderColor by animateColorAsState(
                            if (isSelected) AppTokens.colors.semantic.success else Color.Transparent,
                            label = "border"
                        )

                        val iconTint = AppTokens.colors.icon.primary

                        Column(
                            modifier = modifier
                                .scalableClick(onClick = onClick)
                                .background(AppTokens.colors.background.card, shape)
                                .border(1.dp, borderColor, shape)
                                .padding(
                                    horizontal = AppTokens.dp.checkSelectableCard.large.horizontalPadding,
                                    vertical = AppTokens.dp.checkSelectableCard.large.verticalPadding
                                ),
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(AppTokens.dp.checkSelectableCard.large.horizontalPadding),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Icon(
                                    imageVector = style.icon,
                                    contentDescription = null,
                                    modifier = Modifier.size(AppTokens.dp.checkSelectableCard.large.icon),
                                    tint = iconTint
                                )

                                Column(
                                    modifier = Modifier.weight(1f),
                                    verticalArrangement = Arrangement.spacedBy(AppTokens.dp.contentPadding.text)
                                ) {
                                    Text(
                                        text = style.title,
                                        style = AppTokens.typography.h5(),
                                        color = AppTokens.colors.text.primary
                                    )

                                    Text(
                                        text = style.description,
                                        style = AppTokens.typography.b14Med(),
                                        color = AppTokens.colors.text.secondary
                                    )
                                }
                            }
                        }
                    }
                    
                    //UI
                    CheckSelectableCard(
                        modifier = Modifier.fillMaxWidth(),
                        onSelect = selectProvider,
                        isSelected = isSelected,
                        style = CheckSelectableCardStyle.Large(
                            title = item.title().text(),
                            description = item.description().text(),
                            icon = item.icon(),
                        )
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Immutable
                    data class HomeState(
                        val productList: List<Product>,
                        val isLoading: Boolean = false,
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     @Immutable
                     data class DataList{
                         val list: List<String>
                     }

                     class ViewModel {
                         var dataList: DataList = DataList(listOf())
                         fun removeLastItem() {
                             val newList = dataList.list.toMutableList().apply {
                                 removeLast()
                             }
                             dataList = dataList.copy(
                                 list = newList
                             )
                         }
                     }

                     fun removeLastItem() {
                         dataList.list.removeLast() // <=== you violate your promise of @Immutable!
                     }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    
                    """.trimIndent()
            )

            SpanText(
                text = "In the same way, it will analyze the functions and check if they can be ignored and/or restarted with the annotations:",
                modifier = Modifier
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "How @Immutable Works",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Initial State: The UI renders based on the initial state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Update: A new immutable state object is created to reflect changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition: Compose detects the new state object and updates only the affected parts of the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.IMMUTABLE_WORK,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Immutable work"
            )

            SpanText(
                text = "Restartable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This function serves as a “scope” where recomposition can begin (in other words, this function can be used as an entry point to where Compose can start re-executing code to recompose after state changes).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A composable that is restartable serves as a scope where recomposition can start. In other words, the function can be a point of entry for where Compose can start re-executing code for recomposition after state changes. (This means that whenever this Composable needs to recompose, it will not trigger the recomposition of its parent scope.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Skippable - (Compose is designed to skip re-executing Composables if their inputs haven't changed)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When called during recomposition, compose is able to skip the function if all parameters are equal to their previous values. (If the compiler marks a composable as skippable, Compose can skip it during recomposition if all its arguments are equal with their previous values.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "As we know if a composable contains unstable parameters even if the data is not changed the composable gets recomposed but with this, it will not thanks to the equality checks introduced with it. Another great advantage will be that all the lambdas will be automatically remembered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Might a Composable Not be Skipped?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Change: If a Composable or any of its ancestors have a state variable that has changed and is used by that Composable.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Parent Recomposition: If a parent Composable recomposes, its children will typically be recomposed as well, even if their inputs haven't explicitly changed (though Compose's smartness can sometimes prevent this).",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lambda Updates: If you pass a lambda to a Composable and the lambda itself is recreated on every recomposition, the child Composable will also recompose. This is why using remember for lambdas or using derivedStateOf is important.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Layout Inspector",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use the Layout Inspector in Android Studio to identify recompositions and see which Composables are being skipped. This is your best friend for debugging performance issues.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "RememberInComposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An annotation that can mark constructors, functions, and property getters, to indicate that they must not be called directly inside composition without being remembered. Errors will be raised by a corresponding lint check.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "FrequentlyChangingValue",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "An annotation that can mark functions, and property getters, to indicate that they should not be called directly inside composition, as this may cause frequent recompositions (for example, marking scroll position values and animating values). Warnings are provided by a corresponding lint check.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Recomposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The action of re-executing composable functions when their inputs vary, so they can emit updated information and update the tree. The runtime must have the ability to recompose our composable functions at arbitrary times.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To optimize this recomposition process, Compose runtime marks each function and type with one of several tags that skip functions for lambdas with unchanged parameters, ensuring efficient recomposition.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "For skipping to work, two conditions must be true. First, the parameter's type must be stable, meaning the compiler can guarantee that the value's observable state will not change without Compose being notified. Second, the current value must be equal to the previous value via equals(). When both conditions hold, the composable is marked skippable, and the compiler generates a comparison check before each re-execution.",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Recomposition work: Recomposition is the process by which Jetpack Compose updates the UI when the underlying data changes. When the state used by a composable changes, Compose intelligently re-executes only the parts of the UI tree that depend on that state, avoiding unnecessary updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Lazy composition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazy composition in Jetpack Compose allows you to defer the execution of expensive Composable functions until they are actually needed. It helps optimize performance by only recomposing and rendering UI components when they become visible or relevant to the current state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "@ReadOnlyComposable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "every @Composable function has its own recomposition scope. That means it can read state, react to changes, and trigger UI updates. But what if all you want to do is read a value — no side effects, no state changes, just a quick lookup like grabbing a resource or accessing a CompositionLocal?",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use: It will fail to compile if you try to call a regular @Composable from inside. Documents intent that this function only reads static or ambient data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These helper functions now safely access composition-local or static resources without participating in recomposition — perfect when you need to use them inside non-recomposing scopes like remember or other read-only helpers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://github.com/senseFy/quota-dog.git
            KotlinCode(
                code = """
                    internal val LocalQdColors = staticCompositionLocalOf { QdLightColors }
                    
                    internal val QdLightColors = QdColors(
                        primary = Color(0xFF2F7D5B),
                    )
                    
                    internal val QdDarkColors = QdColors(
                        primary = Color(0xFF6FBE99),
                    )  
                    
                    @Immutable
                    data class QdColors(
                        // Brand / primary action surfaces.
                        val primary: Color,
                    }
                    
                    
                    @Immutable
                    data class QdTypography(
                        val displayLarge: TextStyle,
                        val titleLarge: TextStyle,
                        val titleMedium: TextStyle,
                        val bodyLarge: TextStyle,
                        val bodyMedium: TextStyle,
                        val labelLarge: TextStyle,
                        val caption: TextStyle,
                        val numeric: TextStyle,
                    )

                    internal val QdDefaultTypography = QdTypography(
                        displayLarge = TextStyle(
                            fontSize = 28.sp,
                            lineHeight = 34.sp,
                            fontWeight = FontWeight.Bold,
                            letterSpacing = (-0.4).sp,
                        ),
                        titleLarge = TextStyle(
                            fontSize = 20.sp,
                            lineHeight = 26.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = (-0.2).sp,
                        ),
                        titleMedium = TextStyle(
                            fontSize = 16.sp,
                            lineHeight = 22.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.sp,
                        ),
                        bodyLarge = TextStyle(
                            fontSize = 15.sp,
                            lineHeight = 22.sp,
                            fontWeight = FontWeight.Normal,
                            letterSpacing = 0.1.sp,
                        ),
                        bodyMedium = TextStyle(
                            fontSize = 14.sp,
                            lineHeight = 20.sp,
                            fontWeight = FontWeight.Normal,
                            letterSpacing = 0.1.sp,
                        ),
                        labelLarge = TextStyle(
                            fontSize = 14.sp,
                            lineHeight = 18.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.1.sp,
                        ),
                        caption = TextStyle(
                            fontSize = 12.sp,
                            lineHeight = 16.sp,
                            fontWeight = FontWeight.Medium,
                            letterSpacing = 0.2.sp,
                        ),
                        numeric = TextStyle(
                            fontSize = 13.sp,
                            lineHeight = 18.sp,
                            fontWeight = FontWeight.Medium,
                            letterSpacing = 0.sp,
                        ),
                    )

                    internal val LocalQdTypography = staticCompositionLocalOf { QdDefaultTypography }
                    
                    @Immutable
                    data class QdSpacing(
                        val xxs: Dp = 2.dp,
                        val xs: Dp = 4.dp,
                        val sm: Dp = 8.dp,
                        val md: Dp = 12.dp,
                        val lg: Dp = 16.dp,
                        val xl: Dp = 20.dp,
                        val xxl: Dp = 24.dp,
                        val xxxl: Dp = 32.dp,
                        val huge: Dp = 48.dp,
                    )

                    @Immutable
                    data class QdElevation(
                        val none: Dp = 0.dp,
                        val low: Dp = 2.dp,
                        val medium: Dp = 8.dp,
                        val high: Dp = 18.dp,
                    )

                    internal val LocalQdSpacing = staticCompositionLocalOf { QdSpacing() }
                    internal val LocalQdElevation = staticCompositionLocalOf { QdElevation() }
                    
                 
                    object QdTheme {
                        val colors: QdColors
                            @Composable @ReadOnlyComposable
                            get() = LocalQdColors.current

                        val typography: QdTypography
                            @Composable @ReadOnlyComposable
                            get() = LocalQdTypography.current

                        val shapes: QdShapes
                            @Composable @ReadOnlyComposable
                            get() = LocalQdShapes.current

                        val spacing: QdSpacing
                            @Composable @ReadOnlyComposable
                            get() = LocalQdSpacing.current

                        val elevation: QdElevation
                            @Composable @ReadOnlyComposable
                            get() = LocalQdElevation.current
                    }
                    
                    /**
                     * Wrap the app once at the top level; everything beneath uses [QdTheme]. Internally still
                     * configures M2 [MaterialTheme] so wrapped M2 widgets (TextField, Snackbar, AlertDialog)
                     * pick up the same identity.
                     */
                    @Composable
                    fun QuotaDogTheme(
                        darkTheme: Boolean = isSystemInDarkTheme(),
                        content: @Composable () -> Unit,
                    ) {
                        val qd = if (darkTheme) QdDarkColors else QdLightColors
                        val typo = QdDefaultTypography
                        val shapes = QdDefaultShapes

                        val materialColors = Colors(
                            primary = qd.primary,
                            primaryVariant = qd.primaryHover,
                            secondary = qd.primary,
                            secondaryVariant = qd.primaryHover,
                            background = qd.background,
                            surface = qd.surface,
                            error = qd.danger,
                            onPrimary = qd.onPrimary,
                            onSecondary = qd.onPrimary,
                            onBackground = qd.textPrimary,
                            onSurface = qd.textPrimary,
                            onError = Color.White,
                            isLight = !darkTheme,
                        )

                        val materialTypography = Typography(
                            defaultFontFamily = androidx.compose.ui.text.font.FontFamily.Default,
                            h4 = typo.displayLarge,
                            h5 = typo.titleLarge,
                            h6 = typo.titleMedium,
                            body1 = typo.bodyLarge,
                            body2 = typo.bodyMedium,
                            button = typo.labelLarge,
                            caption = typo.caption,
                        )

                        val materialShapes = Shapes(
                            small = shapes.sm,
                            medium = shapes.md,
                            large = shapes.lg,
                        )

                        CompositionLocalProvider(
                            LocalQdColors provides qd,
                            LocalQdTypography provides typo,
                            LocalQdShapes provides shapes,
                            LocalQdSpacing provides QdSpacing(),
                            LocalQdElevation provides QdElevation(),
                        ) {
                            MaterialTheme(
                                colors = materialColors,
                                typography = materialTypography,
                                shapes = materialShapes,
                                content = content,
                            )
                        }
                    }
                    """.trimIndent()
            )

            //https://github.com/Grippo-app/grippo-mobile.git
            KotlinCode(
                code = """
                    public interface AppColor {
                        public val button: ButtonColors
                        
                        public interface ButtonColors {
                            public val backgroundPrimary1: Color
                        }
                    }
                    
                    public object DarkColor : AppColor {
                        override val button: AppColor.ButtonColors = object : AppColor.ButtonColors {
                            override val backgroundPrimary1 = AppPalette.Unique.Orange
                        }
                    }
                    
                    @Stable
                    public object AppTokens {
                        public val colors: AppColor
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppColors.current

                        public val icons: AppIcon
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppIcons.current

                        public val typography: AppTypography
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppTypography.current

                        public val strings: AppString
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppStrings.current

                        public val drawables: AppDrawable
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppDrawables.current

                        public val dp: AppDp
                            @Composable
                            @ReadOnlyComposable
                            get() = LocalAppDp.current
                    }
                    """.trimIndent()
            )

            //https://github.com/patrykandpatrick/liftapp.git
            KotlinCode(
                code = """
                    //Access
                    contentPadding: PaddingValues =
                        PaddingValues(
                            horizontal = LocalDimens.current.padding.itemHorizontal,
                            vertical = LocalDimens.current.padding.itemVerticalSmall,
                        )
                        
                    //Dimens.kt
                    @Immutable
                    data class Dimens(
                        val button: Button = Button(),
                        val card: Card = Card(),
                        val checkbox: Checkbox = Checkbox(),
                        val chip: Chip = Chip(),
                        val dialog: Dialog = Dialog(),
                        val divider: Divider = Divider(),
                        val elevation: Elevation = Elevation(),
                        val fab: FAB = FAB(),
                        val grid: Grid = Grid(),
                        val iconButton: IconButton = IconButton(),
                        val input: Input = Input(),
                        val list: List = List(),
                        val muscle: Muscle = Muscle(),
                        val padding: Padding = Padding(),
                        val radioButton: RadioButton = RadioButton(),
                        val routine: Routine = Routine(),
                        val searchBar: SearchBar = SearchBar(),
                        val strokeWidth: Dp = 1.dp,
                        val swipe: Swipe = Swipe(),
                        val stepper: Stepper = Stepper(),
                        val tab: Tab = Tab(),
                        val toolbar: Toolbar = Toolbar(),
                        val verticalItemSpacing: Dp = 16.dp,
                    ) {

                        @Immutable
                        data class Padding(
                            val contentHorizontal: Dp = 16.dp,
                            val contentHorizontalSmall: Dp = 8.dp,
                            val contentVertical: Dp = 20.dp,
                            val contentVerticalSmall: Dp = 10.dp,
                            val itemHorizontal: Dp = 16.dp,
                            val itemHorizontalMedium: Dp = 12.dp,
                            val itemHorizontalSmall: Dp = 8.dp,
                            val itemVertical: Dp = 16.dp,
                            val itemVerticalSmall: Dp = 8.dp,
                            val itemVerticalMedium: Dp = 12.dp,
                            val segmentedButtonHorizontal: Dp = 12.dp,
                            val segmentedButtonVertical: Dp = 12.dp,
                            val segmentedButtonElement: Dp = 8.dp,
                            val supportingTextHorizontal: Dp = 16.dp,
                            val supportingTextVertical: Dp = 4.dp,
                            val exercisesControlsVertical: Dp = 8.dp,
                        )

                        @Immutable
                        data class IconButton(
                            val minTouchTarget: Dp = 48.dp,
                            val size: Dp = 40.dp,
                            val rippleRadius: Dp = 20.dp,
                        )

                        @Immutable
                        data class Grid(val minCellWidthMedium: Dp = 164.dp, val minCellWidthLarge: Dp = 240.dp)

                        @Immutable
                        data class Dialog(
                            val minWidth: Dp = 280.dp,
                            val maxWidth: Dp = 560.dp,
                            val paddingMedium: Dp = 16.dp,
                            val paddingLarge: Dp = 24.dp,
                            val tonalElevation: Dp = 6.dp,
                        )

                        @Immutable
                        data class Card(
                            val smallElevation: Dp = 4.dp,
                            val smallCornerRadius: Dp = 16.dp,
                            val contentPaddingHorizontal: Dp = 16.dp,
                            val contentPaddingVertical: Dp = 16.dp,
                        )

                        @Immutable
                        data class Chip(
                            val iconSize: Dp = 18.dp,
                            val spacing: Dp = 4.dp,
                            val horizontalPadding: Dp = 6.dp,
                            val endPadding: Dp = 12.dp,
                            val verticalPadding: Dp = 6.dp,
                            val minHeight: Dp = 20.dp,
                        )

                        @Immutable
                        data class Input(
                            val nameMaxLines: Int = 3,
                            val cornerRadius: Dp = 4.dp,
                            val labelStartMargin: Dp = 12.dp,
                            val labelHorizontalPadding: Dp = 4.dp,
                        )

                        @Immutable
                        data class List(
                            val checkedItemHorizontalPadding: Dp = 4.dp,
                            val checkedItemTonalElevation: Dp = 2.dp,
                            val itemTitleHighlightCornerRadius: Dp = 4.dp,
                            val itemIconBackgroundSize: Dp = 40.dp,
                        )

                        @Immutable
                        data class Muscle(
                            val tileSize: Dp = 20.dp,
                            val tileCornerSize: Dp = 6.dp,
                            val gridCellMinSize: Dp = 164.dp,
                            val listItemHorizontalMargin: Dp = 16.dp,
                        )

                        @Immutable
                        data class Checkbox(val size: Dp = 20.dp, val cornerSize: Dp = 4.dp, val strokeWidth: Dp = 2.dp)

                        @Immutable data class RadioButton(val size: Dp = 20.dp)

                        @Immutable data class Routine(val minCardWidth: Dp = 140.dp)

                        @Immutable data class Tab(val verticalPadding: Dp = 16.dp, val iconToTextPadding: Dp = 4.dp)

                        @Immutable data class Toolbar(val height: Dp = 56.dp)

                        @Immutable data class SearchBar(val verticalPadding: Dp = 12.dp)

                        @Immutable
                        data class Button(
                            val iconPadding: Dp = 8.dp,
                            val horizontalPadding: Dp = 16.dp,
                            val horizontalPaddingNarrow: Dp = 12.dp,
                            val verticalPadding: Dp = 10.dp,
                            val verticalPaddingNarrow: Dp = 8.dp,
                            val borderWidth: Dp = 1.dp,
                            val minContentHeight: Dp = 24.dp,
                            val underlineWidth: Dp = 1.5.dp,
                        )

                        @Immutable
                        data class FAB(
                            val iconPadding: Dp = 12.dp,
                            val horizontalPadding: Dp = 20.dp,
                            val verticalPadding: Dp = 14.dp,
                        )

                        @Immutable data class Elevation(val dragElevation: Dp = 2.dp)

                        @Immutable
                        data class Swipe(
                            val fractionalThreshold: Float = .4f,
                            val backgroundVisibilityThreshold: Dp = 56.dp,
                            val swipeElevation: Dp = 2.dp,
                        )

                        @Immutable
                        data class Stepper(
                            val stepBorderPaddingVertical: Dp = 6.dp,
                            val stepBorderPaddingHorizontal: Dp = 10.dp,
                            val stepIconSize: Dp = 12.dp,
                            val spacing: Dp = 4.dp,
                        )

                        @Immutable
                        data class Divider(
                            val sinPeriodLength: Dp = 3.dp,
                            val sinHeight: Dp = 6.dp,
                            val thickness: Dp = 1.dp,
                        )
                    }

                    val PortraitDimens = Dimens()

                    val LandscapeDimens =
                        Dimens(padding = Dimens.Padding(contentHorizontal = 56.dp, contentHorizontalSmall = 48.dp))

                    val DialogDimens: Dimens
                        @Composable
                        get() =
                            LocalDimens.current.run {
                                copy(padding = padding.copy(contentHorizontal = 24.dp, contentVertical = 24.dp))
                            }

                    val LocalDimens = staticCompositionLocalOf { Dimens() }

                    @Deprecated(
                        "Use `dimens` instead.",
                        ReplaceWith("dimens", "com.patrykandpatryk.liftapp.core.ui.dimens.dimens"),
                    )
                    val MaterialTheme.dimens: Dimens
                        @Composable @ReadOnlyComposable get() = LocalDimens.current

                    val dimens: Dimens
                        @Composable @ReadOnlyComposable get() = LocalDimens.current

                    //
                    @Composable
                    @ReadOnlyComposable
                    fun PaddingValues.calculateStartPadding(): Dp = calculateStartPadding(LocalLayoutDirection.current)

                    @Composable
                    @ReadOnlyComposable
                    fun PaddingValues.calculateEndPadding(): Dp = calculateEndPadding(LocalLayoutDirection.current)

                    fun PaddingValues.horizontal(): Dp =
                        calculateStartPadding(LayoutDirection.Ltr) + calculateEndPadding(LayoutDirection.Ltr)

                    fun PaddingValues.vertical(): Dp = calculateTopPadding() + calculateBottomPadding()

                    @Composable
                    @ReadOnlyComposable
                    fun WindowInsets.getBottom(): Dp = asPaddingValues().calculateBottomPadding()

                    @Composable
                    fun PaddingValues.copy(
                        start: Dp = calculateStartPadding(),
                        top: Dp = calculateTopPadding(),
                        end: Dp = calculateEndPadding(),
                        bottom: Dp = calculateBottomPadding(),
                    ): PaddingValues = PaddingValues(start = start, top = top, end = end, bottom = bottom)

                    @Composable
                    fun PaddingValues.increaseBy(
                        start: Dp = 0.dp,
                        top: Dp = 0.dp,
                        end: Dp = 0.dp,
                        bottom: Dp = 0.dp,
                    ): PaddingValues =
                        copy(
                            start = calculateStartPadding() + start,
                            top = calculateTopPadding() + top,
                            end = calculateEndPadding() + end,
                            bottom = calculateBottomPadding() + bottom,
                        )

                    @Composable
                    fun PaddingValues.increaseBy(horizontal: Dp = 0.dp, vertical: Dp = 0.dp): PaddingValues =
                        increaseBy(start = horizontal, top = vertical, end = horizontal, bottom = vertical)

                    @Composable
                    operator fun PaddingValues.plus(other: PaddingValues): PaddingValues =
                        PaddingValues(
                            start = calculateStartPadding() + other.calculateStartPadding(),
                            top = calculateTopPadding() + other.calculateTopPadding(),
                            end = calculateEndPadding() + other.calculateEndPadding(),
                            bottom = calculateBottomPadding() + other.calculateBottomPadding(),
                        )
                    
                        
                        
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    @ReadOnlyComposable
                    fun getContext(): Context {
                        return LocalContext.current
                    }

                    @Composable
                    @ReadOnlyComposable
                    fun getAppName(): String {
                        return stringResource(R.string.app_name)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Type Compose",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/mofeejegi/compose-alert-banner.git
            KotlinCode(
                code = """
                    sealed class AlertBannerType {
                        @get:Composable
                        abstract val color: Color
                        abstract val icon: DrawableResource

                        data object Error : AlertBannerType() {
                            override val color: Color
                                @Composable get() = AlertTheme.colorScheme.danger

                            override val icon: DrawableResource = Res.drawable.ic_error
                        }

                        data object Success : AlertBannerType() {
                            override val color: Color
                                @Composable get() = AlertTheme.colorScheme.success

                            override val icon: DrawableResource = Res.drawable.ic_check_circle
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@NonRestartableComposable",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For Static UI (No frequent state changes per item) like static headers or static text content.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun NonRestartableContentScreen(modifier: Modifier) {
                        var counterState by remember { mutableStateOf(UiState(0, LocalDateTime.now())) }

                        Column(
                            modifier = modifier.padding(16.dp),
                            horizontalAlignment = androidx.compose.ui.Alignment.CenterHorizontally
                        ) {
                            Header()
                            Spacer(Modifier.height(32.dp))
                            Body(counterState)
                            Spacer(Modifier.height(32.dp))
                            Button(
                                onClick = { counterState = UiState(
                                    counter = counterState.counter + 1,
                                    lastClick = LocalDateTime.now()
                                ) },
                            ) {
                                Text("Increment")
                            }
                        }
                    }

                    @NonRestartableComposable
                    @Composable
                    private fun Header() {
                        Column {
                            Spacer(Modifier.height(32.dp))
                            Text(
                                "Tap the count in oder to increment it",
                                style = MaterialTheme.typography.headlineMedium
                            )
                        }
                    }

                    @Composable
                    private fun Body(counterState: UiState) {
                        Column {
                            Text("Count at: ${counterState.counter}", style = MaterialTheme.typography.bodyLarge)
                            Text("Clicked at: ${counterState.lastClick}", style = MaterialTheme.typography.bodyLarge)
                            Spacer(Modifier.height(16.dp))
                        }
                    }

                    @Immutable
                    data class UiState(val counter: Int, val lastClick: LocalDateTime)
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    @Composable
                    fun ListComponent(listItems: List<String>, modifier: Modifier = Modifier) {
                        LazyColumn(modifier = modifier) {
                            items(count = listItems.count(), key = { "${listItems[it]}-$it" }) { index ->
                                StaticListItem(index = index, title = listItems[index])
                            }
                        }
                    }

                    @NonRestartableComposable
                    @Composable
                    fun StaticListItem(index: Int, title: String) {
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .height(32.dp)) {
                            Text(index.toString(), style = MaterialTheme.typography.bodyMedium)
                            Spacer(Modifier.width(16.dp))
                            Text(title, style = MaterialTheme.typography.titleMedium)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "PausableComposition",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Heavy UI operations can block the main thread.  Pausable Composition is a runtime evolution that allows Compose to split heavy UI work across multiple frames.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun PausableExample() {
                        val composition = rememberPausableCompositionLocal()
                        val paused = remember { mutableStateOf(false) }
                      
                        Button(onClick = { paused.value = !paused.value }) {
                            Text(text = if (paused.value) "Resume" else "Pause")
                        }
                        
                        if (!paused.value) {
                            composition.pause()
                        } else {
                            composition.resume()
                        }
                        // Your heavy UI composition here
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "movableContentOf",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "movableContentOf For Components with frequent appearance/disappearance (e.g., animations, paging, transitions) and conditional visibilities.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    private fun ListComponent(listItemsState: State<List<ListItem>>, modifier: Modifier = Modifier) {
                        val listItemComponent = remember {
                            movableContentOf<ListItem> { item ->
                                MoveableListItemComponent(item)
                            }
                        }

                        val listItems by listItemsState
                        LazyColumn(modifier = modifier) {
                            items(listItems, key = { it.key }) { item ->
                                listItemComponent(item)
                            }
                        }
                    }

                    @Composable
                    private fun MoveableListItemComponent(listItem: ListItem) {
                        var isChecked by rememberSaveable { mutableStateOf(false) }
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .height(64.dp)
                        ) {
                            Text(listItem.index.toString(), style = MaterialTheme.typography.bodyMedium)
                            Spacer(Modifier.width(16.dp))
                            Text(listItem.title, style = MaterialTheme.typography.titleMedium)
                            Spacer(Modifier.weight(1f))
                            Checkbox(
                                checked = isChecked,
                                onCheckedChange = {
                                    isChecked = it
                                }
                            )
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://doveletter.dev/preview/articles/compose-performance-optimization#how-the-compiler-decides-stability